import { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import GenericHeader from "../../../Componentes/GenericHeader";
import SortableTable from "../../../Componentes/Utils/SortableTable";
import FilterContainer from "../../../Componentes/Utils/FilterContainer";
import Filters from "../../../Componentes/Utils/Filters";
import useApi from "../../../hooks/useApi";

const PageContent = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: 10px;
    box-sizing: border-box;
    overflow: hidden;
`;

const TableFlexFill = styled.div`
    flex: 1;
    min-height: 0;
    width: 100%;
    display: flex;
    flex-direction: column;

    & > div {
        flex: 1;
        min-height: 0;
        display: flex;
        flex-direction: column;
    }
`;

const TrimestreResumo = styled.div`
    flex-shrink: 0;
    background-color: #f3f3f3;
    border: 1px solid rgba(0, 0, 0, 0.15);
    border-radius: 12px;
    padding: 10px 16px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    gap: 2px;

    @media (max-width: 768px) {
        width: 100%;
    }
`;

const TrimestreLabel = styled.span`
    font-size: 12px;
    font-weight: bold;
    color: #333;
`;

const TrimestreValor = styled.span`
    font-size: 15px;
    font-weight: bold;
    color: #000;
    white-space: nowrap;
`;

const MESES_PT = [
    "JANEIRO",
    "FEVEREIRO",
    "MARÇO",
    "ABRIL",
    "MAIO",
    "JUNHO",
    "JULHO",
    "AGOSTO",
    "SETEMBRO",
    "OUTUBRO",
    "NOVEMBRO",
    "DEZEMBRO",
];

function formatarMesLabel(valor) {
    if (!valor) return "-";
    const [ano, mes] = String(valor).split("-");
    const idx = Number(mes) - 1;
    if (idx < 0 || idx > 11) return valor;
    return `${MESES_PT[idx]}/${ano}`;
}

function formatPontos(valor) {
    const n = Number(valor ?? 0);
    if (Number.isNaN(n)) return "0,00";
    return n.toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
}

const Content = ({ onTrimestreLoaded }) => {
    const api = useApi();

    const mesAtual = useMemo(() => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    }, []);

    const [inicio, setInicio] = useState(mesAtual);
    const [loading, setLoading] = useState(false);
    const [ranking, setRanking] = useState([]);
    const [periodo, setPeriodo] = useState({ inicio: "", fim: "" });

    const fetchRanking = useCallback(async () => {
        if (!inicio) return;

        setLoading(true);
        try {
            const { data } = await api.get(
                `/Painel/API/Prossmap/Ranking/index.php?inicio=${inicio}`
            );
            const payload = data?.data ?? data;
            setRanking(Array.isArray(payload?.ranking) ? payload.ranking : []);
            setPeriodo({
                inicio: payload?.inicio ?? inicio,
                fim: payload?.fim ?? "",
            });
            if (onTrimestreLoaded) onTrimestreLoaded(payload);
        } catch (error) {
            console.error("Erro ao buscar ranking:", error);
            setRanking([]);
            setPeriodo({ inicio: "", fim: "" });
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inicio]);

    useEffect(() => {
        fetchRanking();
    }, [fetchRanking]);

    const trimestreLabel = useMemo(() => {
        if (!periodo.inicio) return "-";
        return `${formatarMesLabel(periodo.inicio)} — ${formatarMesLabel(periodo.fim)}`;
    }, [periodo]);

    const columns = useMemo(
        () => [
            {
                id: "posicao",
                header: "POSIÇÃO",
                accessor: (row) => `${row.posicao}º`,
                sortable: true,
                width: "90px",
                align: "center",
                sortValue: (row) => Number(row.posicao ?? 0),
            },
            {
                id: "encarregado",
                header: "ENCARREGADO",
                accessor: "encarregado_nome",
                sortable: true,
            },
            {
                id: "matricula",
                header: "MATRÍCULA",
                accessor: "encarregado_matricula",
                sortable: true,
                width: "110px",
            },
            {
                id: "equipe",
                header: "EQUIPE",
                accessor: (row) => row.equipe || "-",
                sortable: true,
            },
            {
                id: "centro_custo",
                header: "CENTRO DE CUSTO",
                accessor: (row) => row.centro_custo || "-",
                sortable: true,
            },
            {
                id: "qtd_prossmaps",
                header: "PROSSMAPS",
                accessor: "qtd_prossmaps",
                sortable: true,
                width: "110px",
                align: "center",
                sortValue: (row) => Number(row.qtd_prossmaps ?? 0),
            },
            {
                id: "pontos_total",
                header: "MÉDIA",
                accessor: (row) => formatPontos(row.pontos_total),
                sortable: true,
                width: "100px",
                align: "center",
                sortValue: (row) => Number(row.pontos_total ?? 0),
            },
        ],
        []
    );

    return (
        <PageContent>
            <GenericHeader responsive>
                <Filters columns={1}>
                    <FilterContainer
                        type="month"
                        placeholder="MÊS/ANO DE INÍCIO"
                        valor={inicio}
                        changeFunc={setInicio}
                    />
                </Filters>

                <TrimestreResumo>
                    <TrimestreLabel>TRIMESTRE</TrimestreLabel>
                    <TrimestreValor>{trimestreLabel}</TrimestreValor>
                </TrimestreResumo>
            </GenericHeader>

            <TableFlexFill>
                <SortableTable
                    columns={columns}
                    data={ranking}
                    isLoading={loading}
                    loadingText="Calculando ranking..."
                    emptyText="Nenhum PROSSMAP encontrado para o trimestre."
                    initialSort={{ key: "pontos_total", direction: "desc" }}
                    getRowId={(row) => row.encarregado_matricula}
                />
            </TableFlexFill>
        </PageContent>
    );
};

export default Content;
